import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  TablePagination
} from '@mui/material';

import { Add, Edit, Delete, Visibility, Close } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../config/api';

const CarsonHeadline = () => {
  const { user } = useAuth();
  const [filteredRows, setFilteredRows] = useState([]);
  const [allHeadlines, setAllHeadlines] = useState([]);
  const [headlineType, setHeadlineType] = useState('CU');
  const [selectedHeadlineId, setSelectedHeadlineId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    description: '',
    link: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ text: '', type: '' });
  const [dialogMessage, setDialogMessage] = useState({ text: '', type: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_ENDPOINTS.HEADLINES}/search?type=${headlineType}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const formattedData = data.map(item => ({
          id: item.headline_id,
          title: item.title,
          description: item.headline,
          link: item.link,
          startDate: item.start_date,
          endDate: item.end_date,
          modifiedDate: item.modified_date
        }));
        setAllHeadlines(formattedData);
        setFilteredRows(formattedData);
      } catch (error) {
        console.error('Error fetching headlines:', error);
        setMessage({ text: 'Failed to load headlines', type: 'error' });
        setAllHeadlines([]);
        setFilteredRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [headlineType]);

  const fetchAllHeadlines = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.HEADLINES}/search?type=${headlineType}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.headline_id,
        title: item.title,
        description: item.headline,
        link: item.link,
        startDate: item.start_date,
        endDate: item.end_date,
        modifiedDate: item.modified_date
      }));
      setAllHeadlines(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      console.error('Error fetching headlines:', error);
      setMessage({ text: 'Failed to load headlines', type: 'error' });
      setAllHeadlines([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  const getHeadline = async (id) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.HEADLINES}/${id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setFormData({
        title: data.title || '',
        startDate: data.start_date || '',
        endDate: data.end_date || '',
        description: data.headline || '',
        link: data.link || ''
      });
      setCharCount(data.headline ? data.headline.length : 0);
      setEditMode(true);
      setEditId(id);
      setOpen(true);
    } catch (error) {
      console.error('Error fetching headline:', error);
      setMessage({ text: 'Failed to load headline details', type: 'error' });
    }
  };

  const handleEdit = (id) => {
    getHeadline(id);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(`${API_ENDPOINTS.HEADLINES}/${selectedHeadlineId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete headline');
      }

      const result = await response.json();
      setMessage({ text: result.message || 'Headline deleted successfully', type: 'success' });
      setSelectedHeadlineId('');
      await fetchAllHeadlines();
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error deleting headline:', error);
      setMessage({ text: error.message || 'Failed to delete headline', type: 'error' });
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after or equal to start date';
    }
    
    if (formData.description && formData.description.length > 3500) {
      newErrors.description = 'Description must be 3500 characters or less';
    }
    
    if (formData.link && formData.link.length > 100) {
      newErrors.link = 'Link must be 100 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setDialogMessage({ text: '', type: '' });
    
    if (!validateForm()) {
      setDialogMessage({ text: 'Please enter the required fields before saving', type: 'error' });
      return;
    }

    setSaveLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const url = editMode ? `${API_ENDPOINTS.HEADLINES}/${editId}` : API_ENDPOINTS.HEADLINES;
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title.trim(),
          startDate: formData.startDate,
          endDate: formData.endDate,
          description: formData.description.trim(),
          link: formData.link.trim(),
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} headline`);
      }

      await fetchAllHeadlines();
      setOpen(false);
      handleClear();
      setMessage({ text: `Headline ${editMode ? 'updated' : 'created'} successfully`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error saving headline:', error);
      setDialogMessage({ 
        text: error.message.includes('already exist') ? 'Headline with this title already exists' : error.message, 
        type: 'error' 
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      title: '',
      startDate: '',
      endDate: '',
      description: '',
      link: ''
    });
    setCharCount(0);
    setErrors({});
    setDialogMessage({ text: '', type: '' });
    setEditMode(false);
    setEditId(null);
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, description: value });
    setCharCount(value.length);
  };

  const handleRowPreview = (row) => {
    setFormData({
      title: row.title,
      startDate: row.startDate,
      endDate: row.endDate,
      description: row.description,
      link: row.link
    });
    setPreviewOpen(true);
  };

  const handleRowDelete = (id) => {
    setSelectedHeadlineId(id.toString());
    setDeleteConfirmOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Carson Headlines</Typography>

      {(loading || saveLoading || deleteLoading) && (
        <LinearProgress sx={{ mb: 2 }} />
      )}
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Headline Type</FormLabel>
          <RadioGroup
            row
            value={headlineType}
            onChange={(e) => {
              setHeadlineType(e.target.value);
              setSearchTerm('');
            }}
          >
            <FormControlLabel value="CU" control={<Radio />} label="Current & Upcoming" />
            <FormControlLabel value="O" control={<Radio />} label="Old" />
          </RadioGroup>
        </FormControl>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={() => { 
            setEditMode(false); 
            setEditId(null); 
            setSelectedHeadlineId('');
            setOpen(true); 
          }}
        >
          Create
        </Button>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <Autocomplete
          sx={{ minWidth: 300 }}
          freeSolo
          options={allHeadlines.map(headline => headline.title)}
          value={searchTerm}
          onInputChange={(event, newValue) => setSearchTerm(newValue || '')}
          onChange={(event, newValue) => {
            setSearchTerm(newValue || '');
            if (newValue) {
              const filtered = allHeadlines.filter(headline =>
                headline.title.toLowerCase().includes(newValue.toLowerCase())
              );
              setFilteredRows(filtered);
            } else {
              setFilteredRows(allHeadlines);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Headlines"
              placeholder="Type to search..."
              size="small"
            />
          )}
        />
      </Box>

      {!loading && (
        <Box sx={{ mt: 2, height: 500, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#1976d2' }}>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>Title</th>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>Description</th>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>Link</th>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>Start Date</th>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>End Date</th>
                <th style={{ padding: '12px', color: 'white', fontWeight: 'bold', textAlign: 'left', borderBottom: 'none' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                <tr 
                  key={row.id}
                  style={{ 
                    backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                  }}
                  onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#e3f2fd'}
                  onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = index % 2 === 0 ? '#fafafa' : 'white'}
                >
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '13px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.title || 'N/A'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '13px', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.description || 'N/A'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '13px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.link || 'N/A'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '13px' }}>{row.startDate || 'N/A'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '13px' }}>{row.endDate || 'N/A'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Visibility 
                        sx={{ cursor: 'pointer', color: 'info.main' }}
                        onClick={() => handleRowPreview(row)}
                      />
                      <Edit 
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => handleEdit(row.id)}
                      />
                      <Delete 
                        sx={{ cursor: 'pointer', color: 'error.main' }}
                        onClick={() => handleRowDelete(row.id)}
                      />
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Box>
      )}

      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Edit Headline' : 'Create Headline'}
          <Close sx={{ cursor: 'pointer' }} onClick={() => { setOpen(false); handleClear(); }} />
        </DialogTitle>
        <DialogContent>
          {dialogMessage.text && (
            <Alert severity={dialogMessage.type} sx={{ mb: 2 }}>
              {dialogMessage.text}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            error={!!errors.title}
            helperText={errors.title}
            sx={{ mb: 2, mt: 1 }}
            inputProps={{ maxLength: 100 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              type="date"
              label="Start Date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              error={!!errors.startDate}
              helperText={errors.startDate}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="date"
              label="End Date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              error={!!errors.endDate}
              helperText={errors.endDate}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description"
            value={formData.description}
            onChange={handleDescriptionChange}
            error={!!errors.description}
            helperText={errors.description || `${charCount}/3500 characters`}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 3500 }}
          />
          <TextField
            fullWidth
            label="Link (Optional)"
            value={formData.link}
            onChange={(e) => setFormData({ ...formData, link: e.target.value })}
            error={!!errors.link}
            helperText={errors.link}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} disabled={saveLoading}>Clear</Button>
          <Button onClick={() => { setOpen(false); handleClear(); }} disabled={saveLoading}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Headline Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
              {formData.title || (selectedHeadlineId ? filteredRows.find(h => h.id === parseInt(selectedHeadlineId))?.title : 'Sample Headline Title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formData.description || (selectedHeadlineId ? filteredRows.find(h => h.id === parseInt(selectedHeadlineId))?.description : 'Sample headline description content...')}
            </Typography>
            {(formData.link || (selectedHeadlineId && filteredRows.find(h => h.id === parseInt(selectedHeadlineId))?.link)) && (
              <Typography variant="body2" color="primary" sx={{ textDecoration: 'underline' }}>
                {formData.link || filteredRows.find(h => h.id === parseInt(selectedHeadlineId))?.link}
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
              Active: {formData.startDate || (selectedHeadlineId ? filteredRows.find(h => h.id === parseInt(selectedHeadlineId))?.startDate : 'YYYY-MM-DD')} - {formData.endDate || (selectedHeadlineId ? filteredRows.find(h => h.id === parseInt(selectedHeadlineId))?.endDate : 'YYYY-MM-DD')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the selected headline?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarsonHeadline;